import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ArrowLeft, Mail, FileText, X, Send, History, FileCode, Archive, ArchiveRestore } from "lucide-react";
import api, { CATEGORIES, CATEGORY_LABEL, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUploader from "@/components/ImageUploader";
import HtmlImportDialog from "@/components/HtmlImportDialog";

const emptyDraft = {
  title: "",
  category: "mindmaps",
  excerpt: "",
  body: "",
  cover_image_url: "",
  published: true,
  author: "SummaryMaps Editorial",
};

export default function AdminDashboardPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [subs, setSubs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin/login");
  }, [user, authLoading, navigate]);

  const loadArticles = useCallback(() => {
    setLoading(true);
    api.get("/admin/articles")
      .then(({ data }) => setArticles(data))
      .catch(() => toast.error("Failed to load articles"))
      .finally(() => setLoading(false));
  }, []);

  const loadSubs = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/newsletter").then(({ data }) => setSubs(data)),
      api.get("/admin/newsletter/runs").then(({ data }) => setRuns(data)).catch(() => setRuns([])),
    ])
      .catch(() => toast.error("Failed to load newsletter data"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    if (tab === "articles") loadArticles();
    else loadSubs();
  }, [tab, user, loadArticles, loadSubs]);

  const openNew = () => { setDraft(emptyDraft); setEditing("new"); };
  const openEdit = (a) => {
    setDraft({
      title: a.title, category: a.category, excerpt: a.excerpt, body: a.body,
      cover_image_url: a.cover_image_url, published: a.published, author: a.author,
    });
    setEditing(a);
  };
  const closeEditor = () => { setEditing(null); setDraft(emptyDraft); };

  const saveDraft = async (e) => {
    e.preventDefault();
    if (!draft.title || !draft.excerpt || !draft.body || !draft.cover_image_url) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/articles", draft);
        toast.success("Article published");
      } else {
        await api.put(`/admin/articles/${editing.id}`, draft);
        toast.success("Article updated");
      }
      closeEditor();
      loadArticles();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Article deleted");
      loadArticles();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleArchive = async (a) => {
    const action = a.archived ? "unarchive" : "archive";
    try {
      await api.post(`/admin/articles/${a.id}/${action}`);
      toast.success(a.archived ? "Article restored" : "Article archived");
      loadArticles();
    } catch {
      toast.error(`${action} failed`);
    }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send-test", { email: testEmail });
      if (data.sent === 0 && data.failed?.length) {
        toast.error(data.failed[0].error || "Test send failed");
      } else {
        toast.success(`Test sent to ${testEmail}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed");
    } finally { setSending(false); }
  };

  const sendNow = async () => {
    if (!window.confirm(`Send the weekly digest to ${subs.length} subscriber(s) now?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send-now");
      toast.success(`Sent to ${data.sent} of ${data.recipient_count}.`);
      if (data.failed?.length) {
        toast.error(`${data.failed.length} failed — check Resend dashboard.`);
      }
      loadSubs();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed");
    } finally { setSending(false); }
  };

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  if (authLoading || !user) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-ink-muted">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" className="text-sm text-gray-500 hover:text-brand inline-flex items-center gap-1.5 shrink-0">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Journal</span>
            </Link>
            <span className="text-gray-300">/</span>
            <span className="font-serif text-lg text-ink truncate">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 hidden md:inline">{user.email}</span>
            <button onClick={handleLogout} className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 grid grid-cols-12 gap-6">
        <aside className="col-span-12 md:col-span-3">
          <nav className="bg-white border border-gray-200 rounded-xl p-2 md:sticky md:top-24 flex md:block gap-2 md:gap-0" data-testid="admin-sidebar">
            <button
              onClick={() => setTab("articles")}
              className={`flex-1 md:w-full flex items-center justify-center md:justify-start gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
                tab === "articles" ? "bg-brand text-white" : "text-gray-700 hover:bg-gray-50"
              }`}
              data-testid="tab-articles"
            >
              <FileText className="w-4 h-4" /> Articles
            </button>
            <button
              onClick={() => setTab("newsletter")}
              className={`flex-1 md:w-full md:mt-1 flex items-center justify-center md:justify-start gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
                tab === "newsletter" ? "bg-brand text-white" : "text-gray-700 hover:bg-gray-50"
              }`}
              data-testid="tab-newsletter"
            >
              <Mail className="w-4 h-4" /> Newsletter
            </button>
          </nav>
        </aside>

        <main className="col-span-12 md:col-span-9">
          {tab === "articles" ? (
            <section>
              <div className="flex items-center justify-between mb-5 gap-3">
                <h1 className="font-serif text-xl sm:text-2xl text-ink truncate">Articles <span className="text-gray-400 text-base font-sans">({articles.length})</span></h1>
                <div className="flex items-center gap-2">
                  <button onClick={() => setImportOpen(true)} className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-white border border-ink/10 hover:border-brand/40 text-sm font-medium text-ink whitespace-nowrap" data-testid="import-html-btn">
                    <FileCode className="w-4 h-4" /> <span className="hidden sm:inline">Upload HTML</span><span className="sm:hidden">HTML</span>
                  </button>
                  <button onClick={openNew} className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-brand text-white hover:bg-brand-hover text-sm font-medium whitespace-nowrap" data-testid="new-article-btn">
                    <Plus className="w-4 h-4" /> <span className="hidden sm:inline">New article</span><span className="sm:hidden">New</span>
                  </button>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium">Title</th>
                        <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Category</th>
                        <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Status</th>
                        <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Updated</th>
                        <th className="text-right px-4 py-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody data-testid="articles-table">
                      {loading ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                      ) : articles.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No articles yet.</td></tr>
                      ) : articles.map((a) => (
                        <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50/60" data-testid={`row-${a.slug}`}>
                          <td className="px-4 py-3 max-w-md">
                            <div className="font-medium text-gray-900 truncate">{a.title}</div>
                            <div className="text-xs text-gray-500 truncate">/{a.slug}</div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-gray-600">{CATEGORY_LABEL[a.category]}</td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                              a.archived ? "bg-amber-50 text-amber-700" : a.published ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"
                            }`}>
                              {a.archived ? "Archived" : a.published ? "Published" : "Draft"}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell text-gray-500">{formatDate(a.updated_at)}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button onClick={() => openEdit(a)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-gray-600 hover:bg-gray-100 text-xs" data-testid={`edit-${a.slug}`}>
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button onClick={() => toggleArchive(a)} className="ml-1 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-amber-700 hover:bg-amber-50 text-xs" data-testid={`archive-${a.slug}`}>
                              {a.archived ? <><ArchiveRestore className="w-3.5 h-3.5" /> Restore</> : <><Archive className="w-3.5 h-3.5" /> Archive</>}
                            </button>
                            <button onClick={() => remove(a)} className="ml-1 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-red-600 hover:bg-red-50 text-xs" data-testid={`delete-${a.slug}`}>
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ) : (
            <section className="space-y-6">
              {/* Send digest controls */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-brand-soft text-brand flex items-center justify-center shrink-0">
                    <Send className="w-5 h-5" />
                  </span>
                  <div className="flex-1">
                    <h2 className="font-serif text-lg text-ink">Weekly digest</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Auto-sends every Monday at 14:00 UTC to all subscribers. Includes articles published in the past 7 days.
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Send a test</label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="flex-1 px-3 py-2.5 rounded-lg border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
                        data-testid="test-email-input"
                      />
                      <button onClick={sendTest} disabled={sending} className="px-4 py-2.5 rounded-lg bg-cream border border-ink/10 text-sm font-medium text-ink hover:border-brand/40 disabled:opacity-60" data-testid="send-test-btn">
                        {sending ? "Sending…" : "Test"}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Send now</label>
                    <button onClick={sendNow} disabled={sending || subs.length === 0} className="w-full px-4 py-2.5 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-hover disabled:opacity-60" data-testid="send-now-btn">
                      Send to {subs.length} subscriber{subs.length === 1 ? "" : "s"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Subscribers */}
              <div>
                <h2 className="font-serif text-xl sm:text-2xl text-ink mb-3">Subscribers <span className="text-gray-400 text-base font-sans">({subs.length})</span></h2>
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200 text-gray-500">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium">Email</th>
                          <th className="text-left px-4 py-3 font-medium">Subscribed</th>
                        </tr>
                      </thead>
                      <tbody data-testid="subscribers-table">
                        {loading ? (
                          <tr><td colSpan={2} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                        ) : subs.length === 0 ? (
                          <tr><td colSpan={2} className="px-4 py-8 text-center text-gray-400">No subscribers yet.</td></tr>
                        ) : subs.map((s) => (
                          <tr key={s.id} className="border-b border-gray-100">
                            <td className="px-4 py-3 text-gray-900 break-all">{s.email}</td>
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(s.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Send history */}
              {runs.length > 0 && (
                <div>
                  <h2 className="font-serif text-xl sm:text-2xl text-ink mb-3 flex items-center gap-2"><History className="w-5 h-5 text-brand" /> Send history</h2>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200 text-gray-500">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium">When</th>
                          <th className="text-left px-4 py-3 font-medium">Subject</th>
                          <th className="text-left px-4 py-3 font-medium">Sent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map((r) => (
                          <tr key={r.id} className="border-b border-gray-100">
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(r.ran_at)}</td>
                            <td className="px-4 py-3 text-gray-900">{r.subject}</td>
                            <td className="px-4 py-3 text-gray-500">{r.sent} / {r.recipient_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {/* HTML import */}
      <HtmlImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onCreated={() => loadArticles()}
      />

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" data-testid="article-editor">
          <div className="bg-white rounded-2xl w-full max-w-3xl my-4 sm:my-8">
            <div className="px-5 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
              <h2 className="font-serif text-lg sm:text-xl text-ink">{editing === "new" ? "New article" : "Edit article"}</h2>
              <button onClick={closeEditor} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500" data-testid="close-editor">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={saveDraft} className="p-5 sm:p-6 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Title</label>
                <input
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  required
                  data-testid="editor-title"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Category</label>
                  <select
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none bg-white"
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    data-testid="editor-category"
                  >
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Author</label>
                  <input
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none"
                    value={draft.author}
                    onChange={(e) => setDraft({ ...draft, author: e.target.value })}
                    data-testid="editor-author"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Cover image</label>
                <ImageUploader value={draft.cover_image_url} onChange={(v) => setDraft({ ...draft, cover_image_url: v })} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Excerpt</label>
                <textarea
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none resize-y"
                  rows={2}
                  value={draft.excerpt}
                  onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
                  required
                  data-testid="editor-excerpt"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Body</label>
                <RichTextEditor value={draft.body} onChange={(v) => setDraft({ ...draft, body: v })} />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={draft.published}
                  onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                  className="rounded"
                  data-testid="editor-published"
                />
                Published
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 pt-2 border-t border-gray-100 mt-2">
                <button type="button" onClick={closeEditor} className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 text-sm" data-testid="editor-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg bg-brand text-white hover:bg-brand-hover text-sm font-medium disabled:opacity-60" data-testid="editor-save">
                  {saving ? "Saving…" : editing === "new" ? "Publish" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
